import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import RadioButton from 'LuxKit/Radio/RadioButton'
import Switch from 'LuxKit/Switch/Switch'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineFilterIcon from 'LuxKit/Icons/line/LineFilterIcon'
import LineSortAmountDownIcon from 'LuxKit/Icons/line/LineSortAmountDownIcon'
import LineSortAmountUpIcon from 'LuxKit/Icons/line/LineSortAmountUpIcon'
import TripListRowItem from './TripListRowItem'
import TypeFilterMenuItem from './TypeFilterMenuItem'
import { TRIP_LIST_GRID_COLUMNS } from './tripListLayout'
import { getTripListTypeLabel } from './tripListTypeLabels'
import TripListCheckboxButton from './TripListCheckboxButton'
import type {
  TripListFilters,
  TripListGroup,
  TripListRow,
  TripListRowType,
  TripListSort,
  TripListSortField,
} from './tripListTypes'

const TYPE_FILTERS: ReadonlyArray<TripListRowType> = [
  'flight',
  'transport',
  'accommodation',
  'cruise',
  'experience',
  'dining',
  'activity',
]

const ListPanel = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${rem(16)} ${rem(20)} ${rem(12)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  flex-shrink: 0;
`

const ToolbarControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.L};
`

const LuxPlusToggle = styled.label`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  height: ${rem(32)};
  padding: 0 ${rem(10)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const ScrollArea = styled.div`
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
  padding: 0 ${rem(20)} ${rem(12)};
`

const HeaderRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: grid;
  grid-template-columns: ${TRIP_LIST_GRID_COLUMNS};
  align-items: center;
  min-height: ${rem(52)};
  min-width: ${rem(760)};
  margin-bottom: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.M};
  background: ${props => props.theme.palette.neutral.default.one};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
`

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
  padding: ${rem(12)} ${rem(8)};

  &:first-child {
    justify-content: center;
  }
`

const HeaderText = styled(BodyText)`
  && {
    font-size: ${rem(15)};
    line-height: ${rem(20)};
  }
`

const HeaderButton = styled(TextButton)`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
  height: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  --button-color: inherit;
  --button-background-color: transparent;
  --button-border-color: transparent;
  --button-hover-color: inherit;
  --button-hover-background-color: transparent;
  --button-hover-border-color: transparent;
  --button-active-color: inherit;
  --button-active-background-color: transparent;
  --button-active-border-color: transparent;
  --button-focus-color: inherit;
  --button-focus-background-color: transparent;
  --button-focus-border-color: transparent;
`

const FilterTriggerButton = styled(HeaderButton)`
  &.is-active {
    gap: ${rem(2)};
    padding: ${rem(4)} ${rem(8)};
    border-radius: ${props => props.theme.borderRadius.round};
    background: ${props => props.theme.palette.highlight.primary.normalBackground};
    color: ${props => props.theme.palette.highlight.primary.normalContrast};
    --button-background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    --button-color: ${props => props.theme.palette.highlight.primary.normalContrast};
    --button-hover-background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    --button-hover-color: ${props => props.theme.palette.highlight.primary.normalContrast};
    --button-active-background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    --button-active-color: ${props => props.theme.palette.highlight.primary.normalContrast};
    --button-focus-background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    --button-focus-color: ${props => props.theme.palette.highlight.primary.normalContrast};
  }
`

const PointerRadioButton = styled(RadioButton)`
  cursor: pointer;

  input {
    cursor: pointer;
  }
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: ${rem(760)};
`

const DayGroup = styled.section`
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const DayHeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${TRIP_LIST_GRID_COLUMNS};
  align-items: center;
  min-height: ${rem(48)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const Cell = styled.div`
  min-width: 0;
  padding: ${rem(10)} ${rem(8)};

  &:first-child {
    display: flex;
    justify-content: center;
  }
`

const CountCell = styled(Cell)`
  grid-column: 5 / -1;
  display: flex;
  justify-content: flex-end;
`

// Keep this as a plain semantic button: LuxKit TextButton applies CTA layout
// styles that break the compact grid header alignment for this disclosure row.
const DayToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${rem(8)};
  min-width: 0;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  margin: 0 ${rem(20)} ${rem(16)};
  padding: ${rem(12)} ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  min-width: ${rem(760)};
  flex-shrink: 0;
`

const CountChip = styled.span`
  padding: ${rem(2)} ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(200)};
  padding: ${rem(24)};
`

const BookAllButton = styled(TextButton)`
  border-radius: ${props => props.theme.borderRadius.round};
`

interface Props {
  groups: Array<TripListGroup>
  allRows: Array<TripListRow>
  visibleRows: Array<TripListRow>
  selectedRowIds: Set<string>
  filters: TripListFilters
  sort: TripListSort | undefined
  luxPlusEnabled: boolean
  onSelectAll: (checked: boolean) => void
  onSelectRows: (rowIds: Array<string>, checked: boolean) => void
  onSelectRow: (rowId: string, checked: boolean) => void
  onTypeFilterToggle: (type: TripListRowType) => void
  onBookableOnlyToggle: () => void
  onClearFilters: () => void
  onSortToggle: (field: TripListSortField) => void
  onSortChange: (sort: TripListSort | undefined) => void
  onLuxPlusToggle: () => void
  onRowClick: (row: TripListRow) => void
  onBookRow: (row: TripListRow) => void
  onBookAll: () => void
  onViewBooking: (row: TripListRow) => void
  onAskAi: (row: TripListRow) => void
  onDelete: (row: TripListRow) => void
  bookingRowId?: string
  actionsDisabled?: boolean
}

function getSortIcon(sort: TripListSort | undefined, field: TripListSortField) {
  if (sort?.field !== field) return null
  return sort.direction === 'asc' ? <LineSortAmountUpIcon size="XS" /> : <LineSortAmountDownIcon size="XS" />
}

function getSelectedTotal(rows: Array<TripListRow>): { total: number; currency: string } | undefined {
  let currency: string | undefined
  let total = 0

  for (const row of rows) {
    const price = row.price.memberPrice ?? row.price.price
    if (price === undefined || !row.price.currency) continue
    if (currency && currency !== row.price.currency) return undefined
    currency = row.price.currency
    total += price
  }

  return currency ? { total, currency } : undefined
}

function TripListView({
  groups,
  allRows,
  visibleRows,
  selectedRowIds,
  filters,
  sort,
  luxPlusEnabled,
  onSelectAll,
  onSelectRows,
  onSelectRow,
  onTypeFilterToggle,
  onBookableOnlyToggle,
  onClearFilters,
  onSortToggle,
  onSortChange,
  onLuxPlusToggle,
  onRowClick,
  onBookRow,
  onBookAll,
  onViewBooking,
  onAskAi,
  onDelete,
  bookingRowId,
  actionsDisabled = false,
}: Props) {
  const intl = useIntl()
  const [typeFilterOpen, setTypeFilterOpen] = useState(false)
  const [priceFilterOpen, setPriceFilterOpen] = useState(false)
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(new Set())
  const typeFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const priceFilterTriggerRef = useRef<HTMLButtonElement>(null)

  const selectedRows = useMemo(
    () => visibleRows.filter(row => selectedRowIds.has(row.id)),
    [selectedRowIds, visibleRows],
  )
  const bookableRows = useMemo(() => visibleRows.filter(row => row.isBookable && !row.isBooked), [visibleRows])
  const bookableTotal = useMemo(() => getSelectedTotal(bookableRows), [bookableRows])
  const allRowsSelected = visibleRows.length > 0 && selectedRows.length === visibleRows.length
  const hasPartialSelection = selectedRows.length > 0 && !allRowsSelected
  const activeTypeFilterCount = filters.types.size + (filters.bookableOnly ? 1 : 0)
  const hasActiveTypeFilter = activeTypeFilterCount > 0
  const hasActivePriceFilter = sort?.field === 'price'
  const rowCountByType = useMemo(() => {
    return allRows.reduce<Record<TripListRowType, number>>((acc, row) => {
      acc[row.rowType] += 1
      return acc
    }, {
      accommodation: 0,
      experience: 0,
      dining: 0,
      flight: 0,
      transport: 0,
      cruise: 0,
      activity: 0,
    })
  }, [allRows])

  const handleSelectAllChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(event.currentTarget.checked)
  }, [onSelectAll])

  const handleTypeSortClick = useCallback(() => {
    onSortToggle('type')
  }, [onSortToggle])

  const handlePriceSortClick = useCallback(() => {
    onSortToggle('price')
  }, [onSortToggle])

  const toggleTypeFilter = useCallback(() => {
    setTypeFilterOpen(prev => !prev)
  }, [])

  const closeTypeFilter = useCallback(() => {
    setTypeFilterOpen(false)
  }, [])

  const togglePriceFilter = useCallback(() => {
    setPriceFilterOpen(prev => !prev)
  }, [])

  const closePriceFilter = useCallback(() => {
    setPriceFilterOpen(false)
  }, [])

  const handlePriceAscClick = useCallback(() => {
    closePriceFilter()
    onSortChange(sort?.field === 'price' && sort.direction === 'asc' ? undefined : { field: 'price', direction: 'asc' })
  }, [closePriceFilter, onSortChange, sort])

  const handlePriceDescClick = useCallback(() => {
    closePriceFilter()
    onSortChange(sort?.field === 'price' && sort.direction === 'desc' ? undefined : { field: 'price', direction: 'desc' })
  }, [closePriceFilter, onSortChange, sort])

  const handleGroupToggle = useCallback((groupId: string) => {
    setCollapsedGroupIds(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }, [])

  const typeLabelByType = useMemo<Record<TripListRowType, string>>(() => ({
    accommodation: getTripListTypeLabel(intl, 'accommodation'),
    experience: getTripListTypeLabel(intl, 'experience'),
    dining: getTripListTypeLabel(intl, 'dining'),
    flight: getTripListTypeLabel(intl, 'flight'),
    transport: getTripListTypeLabel(intl, 'transport'),
    cruise: getTripListTypeLabel(intl, 'cruise'),
    activity: getTripListTypeLabel(intl, 'activity'),
  }), [intl])

  const typeHeaderLabel = intl.formatMessage({
    id: 'RTLU_2os7TlW4Bu0fGMO',
    defaultMessage: 'Type',
    description: 'Type column header label in the trip planner list view',
  })

  return (
    <ListPanel>
      <Toolbar>
        <ToolbarControls>
          <LuxPlusToggle>
            <LuxPlusLogoWrapper height={10} />
            <ProductPaletteProvider palette="lux-plus">
              <Switch checked={luxPlusEnabled} onChange={onLuxPlusToggle} />
            </ProductPaletteProvider>
          </LuxPlusToggle>
        </ToolbarControls>
      </Toolbar>
      <ScrollArea>
        <HeaderRow>
          <HeaderCell>
            <TripListCheckboxButton
              size="small"
              checked={allRowsSelected}
              indeterminate={hasPartialSelection}
              onChange={handleSelectAllChange}
              aria-label={intl.formatMessage({
                id: 'ZeEQT3x-Y6zCUOXzT_ke',
                defaultMessage: 'Select all itinerary items',
                description: 'Accessible label for selecting all visible rows in the trip planner list view',
              })}
            />
          </HeaderCell>
          <HeaderCell>
            <HeaderText variant="small" weight="bold">{typeHeaderLabel}</HeaderText>
            <FilterTriggerButton
              type="button"
              kind="secondary"
              onClick={toggleTypeFilter}
              ref={typeFilterTriggerRef}
              className={hasActiveTypeFilter ? 'is-active' : undefined}
            >
              <LineFilterIcon size="XS" />
              {hasActiveTypeFilter && <Caption variant="medium" weight="bold">{activeTypeFilterCount}</Caption>}
              <LineAngleDownIcon size="XS" />
            </FilterTriggerButton>
            <HeaderButton type="button" kind="secondary" onClick={handleTypeSortClick}>
              {getSortIcon(sort, 'type')}
            </HeaderButton>
            <DropdownList
              size="S"
              open={typeFilterOpen}
              onClose={closeTypeFilter}
              triggerRef={typeFilterTriggerRef}
              anchorRef={typeFilterTriggerRef}
              placement="bottom-start"
              portaled
            >
              {TYPE_FILTERS.map(type => (
                <TypeFilterMenuItem
                  key={type}
                  type={type}
                  label={typeLabelByType[type]}
                  count={rowCountByType[type]}
                  selected={filters.types.has(type)}
                  onToggle={onTypeFilterToggle}
                />
              ))}
              <ClickableListItem
                title={intl.formatMessage({
                  id: 'Z0lGZ1R9f0cNfrErpwD4',
                  defaultMessage: 'Bookable only',
                  description: 'Filter option label for showing only bookable rows in the trip planner list view',
                })}
                size="medium"
                startIcon={<TripListCheckboxButton size="small" checked={filters.bookableOnly} readOnly />}
                onClick={onBookableOnlyToggle}
              />
              <ClickableListItem
                title={intl.formatMessage({
                  id: 'BEzjrYXQeqoRk_KikB7D',
                  defaultMessage: 'Clear filters',
                  description: 'Menu item label for clearing list view filters in the trip planner list view',
                })}
                size="medium"
                onClick={onClearFilters}
              />
            </DropdownList>
          </HeaderCell>
          <HeaderCell>
            <HeaderText variant="small" weight="bold">
              {intl.formatMessage({
                id: 'XQ5cqE6Sne1ahbljGP5b',
                defaultMessage: 'Notes',
                description: 'Notes column header label in the trip planner list view',
              })}
            </HeaderText>
          </HeaderCell>
          <HeaderCell>
            <HeaderText variant="small" weight="bold">
              {intl.formatMessage({
                id: 'ChJyqLBRZb8uxapP1Izz',
                defaultMessage: 'Price',
                description: 'Price column header label in the trip planner list view',
              })}
            </HeaderText>
            <FilterTriggerButton
              type="button"
              kind="secondary"
              onClick={togglePriceFilter}
              ref={priceFilterTriggerRef}
              className={hasActivePriceFilter ? 'is-active' : undefined}
            >
              <LineFilterIcon size="XS" />
              {hasActivePriceFilter && <Caption variant="medium" weight="bold">{1}</Caption>}
              <LineAngleDownIcon size="XS" />
            </FilterTriggerButton>
            <HeaderButton type="button" kind="secondary" onClick={handlePriceSortClick}>
              {getSortIcon(sort, 'price')}
            </HeaderButton>
            <DropdownList
              size="S"
              open={priceFilterOpen}
              onClose={closePriceFilter}
              triggerRef={priceFilterTriggerRef}
              anchorRef={priceFilterTriggerRef}
              placement="bottom-start"
              portaled
            >
              <ClickableListItem
                title={intl.formatMessage({
                  id: 'tJpeLOhiW3Y_Acqju5D4',
                  defaultMessage: 'Price: low to high',
                  description: 'Menu option for sorting trip planner list rows from low to high price',
                })}
                size="medium"
                startIcon={<PointerRadioButton size="small" checked={sort?.field === 'price' && sort.direction === 'asc'} readOnly />}
                onClick={handlePriceAscClick}
              />
              <ClickableListItem
                title={intl.formatMessage({
                  id: 'Rqi1QPbw38QKh6kZMpFW',
                  defaultMessage: 'Price: high to low',
                  description: 'Menu option for sorting trip planner list rows from high to low price',
                })}
                size="medium"
                startIcon={<PointerRadioButton size="small" checked={sort?.field === 'price' && sort.direction === 'desc'} readOnly />}
                onClick={handlePriceDescClick}
              />
            </DropdownList>
          </HeaderCell>
          <HeaderCell>
            <HeaderText variant="small" weight="bold">
              {intl.formatMessage({
                id: 'qsk9rgEPpUaMK_FgJWbW',
                defaultMessage: 'Action',
                description: 'Action column header in the trip planner list view',
              })}
            </HeaderText>
          </HeaderCell>
          <HeaderCell />
        </HeaderRow>
        {groups.length === 0 && (
          <EmptyState>
            <BodyText variant="medium" colour="light-foreground-alt">
              {intl.formatMessage({
                id: '_HhYADAgKgwTYQp743JX',
                defaultMessage: 'No itinerary items match these filters.',
                description: 'Empty state shown when the trip planner list view has no rows after filtering',
              })}
            </BodyText>
          </EmptyState>
        )}
        {groups.length > 0 && (
          <ContentStack>
            {groups.map(group => {
              const isCollapsed = collapsedGroupIds.has(group.id)
              const groupSelectedRows = group.rows.filter(row => selectedRowIds.has(row.id))
              const isGroupSelected = group.rows.length > 0 && groupSelectedRows.length === group.rows.length
              const isGroupPartiallySelected = groupSelectedRows.length > 0 && !isGroupSelected
              const handleGroupCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                onSelectRows(group.rows.map(row => row.id), event.currentTarget.checked)
              }
              const handleToggleGroup = () => {
                handleGroupToggle(group.id)
              }

              return (
                <DayGroup key={group.id}>
                  <DayHeaderRow>
                    <Cell>
                      <TripListCheckboxButton
                        size="small"
                        checked={isGroupSelected}
                        indeterminate={isGroupPartiallySelected}
                        onChange={handleGroupCheckboxChange}
                        aria-label={intl.formatMessage(
                          {
                            id: 'bTytPCRUIZ8yy3PNaLVE',
                            defaultMessage: 'Select all items in {groupTitle}',
                            description: 'Accessible label for selecting all rows in one trip planner list group',
                          },
                          { groupTitle: group.title },
                        )}
                      />
                    </Cell>
                    <Cell>
                      <DayToggle type="button" onClick={handleToggleGroup}>
                        {isCollapsed ? <LineAngleRightIcon size="XS" /> : <LineAngleDownIcon size="XS" />}
                        <BodyText variant="medium" weight="bold" lineClamp={1}>{group.title}</BodyText>
                        {group.subtitle && <Caption variant="large" colour="light-foreground-alt" lineClamp={1}>{group.subtitle}</Caption>}
                      </DayToggle>
                    </Cell>
                    <Cell />
                    <Cell />
                    <CountCell>
                      <Caption variant="large" colour="light-foreground-alt">
                        {intl.formatMessage(
                          {
                            id: 'yvwVSYWkkzaSUE3Su08O',
                            defaultMessage: '{count, plural, one {# item} other {# items}}',
                            description: 'Item count shown for a group of rows in the trip planner list view',
                          },
                          { count: group.rows.length },
                        )}
                      </Caption>
                    </CountCell>
                  </DayHeaderRow>
                  {!isCollapsed && group.rows.map(row => (
                    <TripListRowItem
                      key={row.id}
                      row={row}
                      selected={selectedRowIds.has(row.id)}
                      luxPlusEnabled={luxPlusEnabled}
                      onSelectRow={onSelectRow}
                      onRowClick={onRowClick}
                      onBookRow={onBookRow}
                      onViewBooking={onViewBooking}
                      onAskAi={onAskAi}
                      onDelete={onDelete}
                      isBookingPending={bookingRowId === row.id}
                      actionsDisabled={actionsDisabled}
                    />
                  ))}
                </DayGroup>
              )
            })}
          </ContentStack>
        )}
      </ScrollArea>
      <Footer>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: '8uvOgLtRnFqsFl34bx7u',
              defaultMessage: 'Bookable through Luxury Escapes',
              description: 'Footer label describing rows that can be booked through Luxury Escapes',
            })}
          </BodyText>
          <CountChip>
            <Caption variant="large">
              {intl.formatMessage(
                {
                  id: 'TtDyDrOLvud-zyDhBmK8',
                  defaultMessage: '{count, plural, one {# item} other {# items}}',
                  description: 'Footer count of bookable itinerary items in the trip planner list view',
                },
                { count: bookableRows.length },
              )}
            </Caption>
          </CountChip>
        </Group>
        <Group direction="horizontal" gap={16} verticalAlign="center">
          {selectedRows.length > 0 && (
            <BodyText variant="medium" colour="light-foreground-alt">
              {intl.formatMessage(
                {
                  id: 'KN0QKCcZyMWKUhcPSWOa',
                  defaultMessage: '{count, plural, =0 {No items selected} one {# item selected} other {# items selected}}',
                  description: 'Selected item count shown in the trip planner list footer',
                },
                { count: selectedRows.length },
              )}
            </BodyText>
          )}
          {bookableTotal && (
            <BodyText variant="large" weight="bold">
              {intl.formatMessage(
                {
                  id: 'fCl67uqL0-s9T_lFwMX6',
                  defaultMessage: 'Total {amount}',
                  description: 'Total selected row price shown in the trip planner list footer',
                },
                {
                  amount: <FormatCurrency value={bookableTotal.total} currency={bookableTotal.currency} />,
                },
              )}
            </BodyText>
          )}
          <BookAllButton size="medium" kind="primary" disabled={actionsDisabled} onClick={onBookAll}>
            {intl.formatMessage({
              id: 'pRxQObCdrNpxcYUl2ccY',
              defaultMessage: 'Book all',
              description: 'CTA button label for booking selected rows in the trip planner list view',
            })}
          </BookAllButton>
        </Group>
      </Footer>
    </ListPanel>
  )
}

export default TripListView
